// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useInstanceResources, useUpdateResources } from '../../hooks/useInstances'
import type { Instance } from '../../api/client'
import { extractApiError } from './shared'
import { Alert, Badge, Button, Card, CardHeader, Field, Input } from '../../components/ui'

/**
 * One limit cell. Read mode is a plain caption + value; edit mode is a real
 * `Field` + `Input`, so the label is wired to the control it names. `size="sm"`
 * is what keeps the three-across grid as dense as it was.
 */
function LimitField({
  label, editing, display, value, onChange, min, max, step,
}: {
  label: string
  editing: boolean
  display: string
  value: string
  onChange: (v: string) => void
  min: string
  max: string
  step?: string
}) {
  if (!editing) {
    return (
      <div>
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <div className="text-sm font-medium text-gray-900">{display}</div>
      </div>
    )
  }
  return (
    <Field label={label}>
      {(props) => (
        <Input
          {...props}
          size="sm"
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={min}
          max={max}
          step={step}
        />
      )}
    </Field>
  )
}

export function DockerTab({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const { data: resources, isLoading } = useInstanceResources(instance.id, true)
  const updateMutation = useUpdateResources(instance.id)
  const [editing, setEditing] = useState(false)
  const [odooMem, setOdooMem] = useState('')
  const [odooCpu, setOdooCpu] = useState('')
  const [odooPids, setOdooPids] = useState('')
  const [pgMem, setPgMem] = useState('')
  const [pgPids, setPgPids] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (resources) {
      setOdooMem(String(resources.odoo.memory_limit_mb))
      setOdooCpu(String(resources.odoo.cpu_cores))
      setOdooPids(String(resources.odoo.pids_limit))
      setPgMem(String(resources.pg.memory_limit_mb))
      setPgPids(String(resources.pg.pids_limit))
    }
  }, [resources])

  if (isLoading || !resources) {
    return <div className="text-sm text-gray-500">{t('common.loading')}</div>
  }

  const handleSave = () => {
    updateMutation.mutate({
      odoo: {
        memory_limit_mb: Number(odooMem),
        cpu_cores: Number(odooCpu),
        pids_limit: Number(odooPids),
      },
      pg: {
        memory_limit_mb: Number(pgMem),
        pids_limit: Number(pgPids),
      },
    }, {
      onSuccess: () => {
        setEditing(false)
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      },
    })
  }

  const handleReset = () => {
    updateMutation.mutate({ odoo: null, pg: null }, {
      onSuccess: () => {
        setEditing(false)
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      },
    })
  }

  const cancelEdit = () => {
    setEditing(false)
    setOdooMem(String(resources.odoo.memory_limit_mb))
    setOdooCpu(String(resources.odoo.cpu_cores))
    setOdooPids(String(resources.odoo.pids_limit))
    setPgMem(String(resources.pg.memory_limit_mb))
    setPgPids(String(resources.pg.pids_limit))
  }

  // yellow-100/700 was the only "yellow" left in this file; the palette
  // contract folds yellow into the warning role, which is amber.
  const badge = (overridden: boolean) => (
    <Badge tone={overridden ? 'warning' : 'neutral'} className="ml-2 align-middle">
      {overridden ? t('detail.docker.override', 'custom') : t('detail.docker.default', 'default')}
    </Badge>
  )

  return (
    <div className="space-y-4">
      <Alert tone="warning">
        {t('detail.docker.restartWarning', 'Changes require a container restart to take effect.')}
      </Alert>

      {saved && (
        <Alert tone="success">{t('detail.docker.saved', 'Resource configuration saved.')}</Alert>
      )}

      {updateMutation.error && (
        <Alert tone="danger">
          {extractApiError(updateMutation.error) || t('common.error', 'An error occurred')}
        </Alert>
      )}

      <Card>
        <CardHeader
          className="mb-4"
          title={<>{t('detail.docker.odooSection', 'Odoo Container')}{badge(resources.odoo.is_overridden)}</>}
        />
        <div className="grid grid-cols-3 gap-4">
          <LimitField
            label={t('detail.docker.memoryMb', 'Memory Limit (MB)')}
            editing={editing}
            display={`${resources.odoo.memory_limit_mb} MB`}
            value={odooMem}
            onChange={setOdooMem}
            min="256"
            max="16384"
          />
          <LimitField
            label={t('detail.docker.cpuCores', 'CPU Cores')}
            editing={editing}
            display={String(resources.odoo.cpu_cores)}
            value={odooCpu}
            onChange={setOdooCpu}
            min="0.25"
            max="8"
            step="0.25"
          />
          <LimitField
            label={t('detail.docker.pidsLimit', 'Process Limit')}
            editing={editing}
            display={String(resources.odoo.pids_limit)}
            value={odooPids}
            onChange={setOdooPids}
            min="64"
            max="4096"
          />
        </div>
      </Card>

      <Card>
        <CardHeader
          className="mb-4"
          title={<>{t('detail.docker.pgSection', 'PostgreSQL Container')}{badge(resources.pg.is_overridden)}</>}
        />
        <div className="grid grid-cols-3 gap-4">
          <LimitField
            label={t('detail.docker.memoryMb', 'Memory Limit (MB)')}
            editing={editing}
            display={`${resources.pg.memory_limit_mb} MB`}
            value={pgMem}
            onChange={setPgMem}
            min="256"
            max="16384"
          />
          <LimitField
            label={t('detail.docker.pidsLimit', 'Process Limit')}
            editing={editing}
            display={String(resources.pg.pids_limit)}
            value={pgPids}
            onChange={setPgPids}
            min="64"
            max="4096"
          />
          <div />
        </div>
      </Card>

      <div className="flex gap-3">
        {editing ? (
          <>
            <Button onClick={handleSave} loading={updateMutation.isPending}>
              {t('detail.docker.save', 'Save')}
            </Button>
            <Button variant="secondary" onClick={cancelEdit}>
              {t('detail.docker.cancel', 'Cancel')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('detail.docker.edit', 'Edit')}
            </Button>
            {(resources.odoo.is_overridden || resources.pg.is_overridden) && (
              <Button variant="secondary" onClick={handleReset} loading={updateMutation.isPending}>
                {t('detail.docker.resetDefaults', 'Reset to Defaults')}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
